import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { Customer } from '../data-models/customer.model';
import { CustomerRequest } from '../request-models/customer-request.model';
import { toCustomerRequest } from '../utils/customer-request-utils';
import { OnboardingService } from '../api-services/onboarding.service';
import { OrganizationContextService } from '../services/organization-context.service';

/**
 * Customers of the current organization: `/customers[/:id]` (hub `user-customers-controller.ts`).
 * The hub scopes every route to the organization the auth interceptor sends.
 *
 * Holds the loaded customers as signal state. Failures set `error` (the hub's message) and resolve to
 * `null` / `false` rather than rejecting.
 */
@Injectable({
  providedIn: 'root'
})
export class CustomerService extends BaseService<Customer> {
  private onboardingService = inject(OnboardingService);
  private organizationContext = inject(OrganizationContextService);

  // Signal-based state management
  private customersSignal = signal<Customer[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Public computed signals for components
  readonly customers = computed(() => this.customersSignal().filter(c => !c.deletedAt));
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  constructor() {
    super('customers', Customer);
    // React to organization context changes — load customers when org becomes available
    effect(() => {
      const orgId = this.organizationContext.currentOrganizationId();
      if (orgId) {
        this.loadCustomers();
      }
    });
  }

  /**
   * Load one page of customers (the hub caps `limit` at 100). Keeps the current list on failure.
   */
  async loadCustomers(search?: string, page: number = 1, limit: number = 100): Promise<void> {
    if (!this.organizationContext.getCurrentOrganizationId()) {
      return;
    }

    const result = await this.track('Failed to load customers', () =>
      firstValueFrom(this.getPage({ query: { search: search || undefined, page, limit } }))
    );
    if (result) {
      this.customersSignal.set(result.items);
    }
  }

  /**
   * Get all customers (from signal)
   */
  getCustomers(): Customer[] {
    return this.customers();
  }

  /**
   * Get customer by ID
   */
  getCustomerById(id: string): Customer | undefined {
    return this.customersSignal().find(c => c.id === id && !c.deletedAt);
  }

  /**
   * Get customer by ID from API
   */
  async fetchCustomerById(id: string): Promise<Customer | null> {
    return this.track('Failed to fetch customer', () => firstValueFrom(this.get(id)));
  }

  /**
   * Create a new customer
   */
  async createCustomer(dto: CustomerRequest): Promise<Customer | null> {
    const isFirstCustomer = this.customers().length === 0;
    const created = await this.track('Failed to create customer', () =>
      firstValueFrom(this.create(toCustomerRequest(dto)))
    );
    if (!created) {
      return null;
    }

    this.customersSignal.update(customers => [...customers, created]);
    // Mark onboarding checklist item as complete if this is the first customer
    if (isFirstCustomer) {
      // completeItem is a cold Observable: without subscribe() no request is sent.
      this.onboardingService.completeItem('add_customers').subscribe();
    }
    return created;
  }

  /**
   * Update an existing customer. Send `null` for an optional field the user emptied.
   */
  async updateCustomer(id: string, dto: CustomerRequest): Promise<Customer | null> {
    const saved = await this.track('Failed to update customer', () =>
      firstValueFrom(this.update(id, toCustomerRequest(dto)))
    );
    if (saved) {
      this.customersSignal.update(customers => customers.map(c => (c.id === id ? saved : c)));
    }
    return saved;
  }

  /**
   * Delete (soft-delete) a customer
   */
  async deleteCustomer(id: string): Promise<boolean> {
    const deleted = await this.track('Failed to delete customer', async () => {
      await firstValueFrom(this.delete(id));
      return true;
    });
    if (deleted) {
      this.customersSignal.update(customers => customers.filter(c => c.id !== id));
    }
    return deleted ?? false;
  }

  /**
   * Get customer options for dropdowns
   */
  getCustomerOptions(): { id: string; name: string }[] {
    return this.customers().map(c => ({
      id: c.id,
      name: c.name ?? ''
    }));
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorSignal.set(null);
  }

  /**
   * Runs an action with the loading flag set. A failure records the hub's message (or `fallback`) in `error`
   * and resolves to `null`.
   */
  private async track<R>(fallback: string, action: () => Promise<R>): Promise<R | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    try {
      return await action();
    } catch (error: unknown) {
      const message = ApiError.fromHttpError(error).errors[0] ?? fallback;
      console.error(`[CustomerService] ${fallback}:`, message);
      this.errorSignal.set(message);
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }
}
